import React, { useCallback } from "react";
import { Box, Typography, IconButton, Paper, Grid } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useDropzone } from "react-dropzone";

interface MultipleImageUploadInputProps {
    value?: (File | string)[];
    onChange?: (images: (File | string)[]) => void;
    label?: string;
    maxImages?: number;
}

const MultipleImageUploadInput = ({
    value = [],
    onChange,
    label = "Drop images here or click to upload",
    maxImages = 10,
}: MultipleImageUploadInputProps) => {
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const newImages = acceptedFiles
                .filter((file) =>
                    ["image/png", "image/jpeg", "image/jpg"].includes(
                        file?.type,
                    ),
                )
                .map((file) => file);
            if (newImages.length > 0) {
                const totalImages = value.length + newImages.length;
                const imagesToAdd =
                    totalImages > maxImages
                        ? newImages.slice(0, maxImages - value.length)
                        : newImages;
                onChange?.([...value, ...imagesToAdd]);
            }
        },
        [value, onChange, maxImages],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
        },
    });

    const handleRemoveImage = (index: number) => {
        const newImages = value.filter((_, i) => i !== index);
        onChange?.(newImages);
    };

    const getImageUrl = (image: File | string): string | null => {
        if (typeof image === "string") {
            return image;
        }
        try {
            return URL.createObjectURL(image);
        } catch {
            return null;
        }
    };

    const canAddMore = value.length < maxImages;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Image Grid */}
            {value.length > 0 && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                    }}
                >
                    <Grid container spacing={1.5}>
                        {value.map((image, index) => {
                            const imageUrl = getImageUrl(image);
                            return (
                                <Grid
                                    size={{ xs: 6, sm: 4, md: 2.4 }}
                                    key={index}
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            width: "100%",
                                            paddingTop: "100%",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            overflow: "hidden",
                                            bgcolor: "grey.50",
                                            "&:hover": {
                                                "& .delete-button": {
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    >
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={`Upload ${index + 1}`}
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => {
                                                    window.open(
                                                        imageUrl,
                                                        "_blank",
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "text.secondary",
                                                    fontSize: 12,
                                                }}
                                            >
                                                No Image
                                            </Box>
                                        )}
                                        <IconButton
                                            className="delete-button"
                                            onClick={() =>
                                                handleRemoveImage(index)
                                            }
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                bgcolor: "error.main",
                                                color: "white",
                                                opacity: 0,
                                                transition: "opacity 0.2s",
                                                width: 24,
                                                height: 24,
                                                "&:hover": {
                                                    bgcolor: "error.dark",
                                                },
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {/* Upload Area */}
            {canAddMore && (
                <Box sx={{ borderWidth: 0 }}>
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: "2px dashed",
                            borderColor: isDragActive
                                ? "warning.main"
                                : "divider",
                            borderRadius: 1,
                            p: 3,
                            textAlign: "center",
                            cursor: "pointer",
                            bgcolor: isDragActive
                                ? "action.hover"
                                : "background.paper",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <input {...getInputProps()} />
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 1.5,
                            }}
                        >
                            <AddIcon
                                sx={{
                                    fontSize: 24,
                                }}
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                color: "text.primary",
                                mb: 0.5,
                            }}
                        >
                            {isDragActive ? "Drop images here" : label}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                        >
                            PNG, JPG ({value.length}/{maxImages} images)
                        </Typography>
                    </Box>
                </Box>
            )}

            {!canAddMore && (
                <Box
                    sx={{
                        p: 2,
                        bgcolor: "warning.light",
                        borderRadius: 1,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: "warning.dark" }}
                    >
                        Maximum {maxImages} images allowed. Please remove some
                        images to add more.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default MultipleImageUploadInput;
